import { UserProfile } from "@dynamic-labs/sdk-react-core";

const VITE_SERVER_URL = import.meta.env.VITE_SERVER_URL;

// PROJECTS
export const getProject = (projectId: number): Promise<ProjectObject> =>
  fetch(`${VITE_SERVER_URL}/projects/${projectId}`, {
    credentials: "include",
  }).then((response) => {
    if (!response.ok) {
      throw new Error("Failed to fetch project");
    }
    return response.json();
  });

export const getProjects = (): Promise<ProjectObject[]> =>
  fetch(`${VITE_SERVER_URL}/projects`, {
    credentials: "include",
  }).then((response) => {
    if (!response.ok) {
      throw new Error("Failed to fetch project");
    }
    return response.json();
  });

export const createProject = (body: any) =>
  fetch(`${VITE_SERVER_URL}/project`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    credentials: "include",
  });

export const updateProject = (projectId: number | string, body: any) => {
  const projectIdParsed =
    typeof projectId === "string" ? parseInt(projectId, 10) : projectId;
  return fetch(`${VITE_SERVER_URL}/project/${projectIdParsed}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    credentials: "include",
  });
};

export const deleteProject = (projectId: number) =>
  fetch(`${VITE_SERVER_URL}/project/${projectId}`, {
    method: "DELETE",
    credentials: "include",
  });

// PROJECT UPDATES
export const getProjectUpdates = (
  projectId: number,
): Promise<ProjectUpdate[]> =>
  fetch(`${VITE_SERVER_URL}/project_updates/${projectId}`, {
    credentials: "include",
  }).then((response) => {
    if (!response.ok) {
      throw new Error("Failed to fetch project updates");
    }
    return response.json();
  });

interface ProjectUpdatesPayload {
  projectId: number;
  bundleUrl: string;
  checksum: string;
  projectUpdateId?: number;
}

export const makeCurrentBuild = async ({
  projectId,
  bundleUrl,
  checksum,
  projectUpdateId,
}: ProjectUpdatesPayload) => {
  const response = await fetch(
    `${VITE_SERVER_URL}/project_updates/${projectId}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        bundleUrl,
        checksum,
        projectUpdateId,
      }),
      credentials: "include",
    },
  );

  if (!response.ok) {
    throw new Error("Failed to make current build");
  }

  return response.json();
};

export const createProjectUpdate = async ({
  projectId,
  bundleUrl,
  checksum,
}: ProjectUpdatesPayload) => {
  const response = await fetch(
    `${VITE_SERVER_URL}/project_updates/${projectId}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ bundleUrl, checksum }),
      credentials: "include",
    },
  );

  if (!response.ok) {
    throw new Error("Failed to create project update");
  }

  return response.json();
};

// TRIGGER BUILD
interface TriggerBuildProps {
  projectId: number;
  url?: string;
}
export const triggerBuild = async (payload: TriggerBuildProps) => {
  const response = await fetch(`${VITE_SERVER_URL}/trigger-build`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error("Failed to trigger new build");
  }

  return response.json();
};

// LOGS
export async function getLogs(pipelineId: string) {
  const response = await fetch(
    `${VITE_SERVER_URL}/circleci/logs/${pipelineId}`,
    {
      credentials: "include",
    },
  );

  if (!response.ok) {
    throw new Error("Failed to fetch logs");
  }

  return response.json();
}

// UPLOAD FOLDER
export async function uploadFolder(formData: FormData) {
  const response = await fetch(`${VITE_SERVER_URL}/upload-folder`, {
    method: "POST",
    body: formData,
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error("Failed to upload folder.");
  }

  return response.json();
}

// CONTENT NODES
interface AvailableNodesResponse {
  nodes: ContentNode[];
  totalNodes: number;
}

interface GetAvailableNodesParams {
  page?: number;
  pageSize?: number;
}

export const getAvailableNodes = async ({
  page = 1,
  pageSize = 10,
}: GetAvailableNodesParams = {}): Promise<AvailableNodesResponse> => {
  const response = await fetch(`${VITE_SERVER_URL}/nodes/available`, {
    credentials: "include",
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || "Failed to fetch available nodes");
  }

  const data = await response.json();

  // Implement client-side pagination
  const startIndex = (page - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedNodes = data.nodes.slice(startIndex, endIndex);

  return {
    nodes: paginatedNodes,
    totalNodes: data.totalNodes,
  };
};

// NODE RESERVATIONS
interface ReserveNodesPayload {
  projectId: number;
  nodeIds: string[];
  spot?: boolean;
  renew?: boolean;
}

export const reserveNodes = async ({
  projectId,
  nodeIds,
  spot = true,
  renew = true,
}: ReserveNodesPayload) => {
  const response = await fetch(`${VITE_SERVER_URL}/nodes/reservations`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ projectId, nodeIds, spot, renew }),
    credentials: "include",
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || "Failed to reserve nodes");
  }

  return response.json();
};

export const deleteNodeReservation = async (
  projectId: number,
  nodeId: string,
): Promise<void> => {
  try {
    const response = await fetch(
      `${VITE_SERVER_URL}/nodes/reservations/${projectId}/${nodeId}`,
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      },
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Error deleting node reservation");
    }

    const data = await response.json();
    console.log("Node reservation successfully deleted:", data);
  } catch (error) {
    console.error("Error deleting reservation:", error);
    throw error;
  }
};

interface DomainCheckResponse {
  valid: boolean;
  type: "A" | "CNAME";
  configured: boolean;
}

export const checkDomainConfiguration = async (
  domain: string,
  type: "A" | "CNAME",
): Promise<DomainCheckResponse> => {
  const response = await fetch(
    `${VITE_SERVER_URL}/project/domain-check/${domain}?type=${type}`,
    {
      credentials: "include",
    },
  );

  if (!response.ok) {
    throw new Error("Failed to check domain configuration");
  }

  return response.json();
};

interface AddCustomDomainResponse {
  success?: boolean;
  error?: string;
}

export const addCustomDomain = async (
  projectId: number,
  customDomain: string,
): Promise<AddCustomDomainResponse> => {
  const response = await fetch(
    `${VITE_SERVER_URL}/project/${projectId}/custom-domain`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ customDomain }),
      credentials: "include",
    },
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || "Failed to add custom domain");
  }

  return response.json();
};

export const deleteCustomDomain = async (
  projectId: number,
  customDomain: string,
): Promise<AddCustomDomainResponse> => {
  const response = await fetch(
    `${VITE_SERVER_URL}/project/${projectId}/custom-domain`,
    {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ customDomain }),
      credentials: "include",
    },
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || "Failed to delete custom domain");
  }

  return response.json();
};

// FEATURE FLAGS
export const getFeatureFlags = async (): Promise<FeatureFlags> => {
  const response = await fetch(`${VITE_SERVER_URL}/feature-flags`, {
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error("Failed to fetch feature flags");
  }

  return response.json();
};

// GITHUB ENDPOINTS
export const analyzeGithubRepo = async (repoUrl: string) => {
  console.log("Analyzing Github repo:", repoUrl);
  const response = await fetch(`${VITE_SERVER_URL}/github/analyze`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ repoUrl }),
    credentials: "include",
  });
  return response.json();
};

interface ExecutePayload {
  calls: {
    contract: string;
    function: string;
    args: any[];
  }[];
}

// EXECUTE CALLS WITH ZERODEV via CLI
export const executeCLI = async (payload: ExecutePayload) => {
  const response = await fetch(`${VITE_SERVER_URL}/execute`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
    credentials: "include",
  });
  return response.json();
};

// COMMUNITY MIRRORS (Public endpoints)
export const getCommunityMirrors = async (): Promise<
  CommunityMirrorProject[]
> => {
  const response = await fetch(`${VITE_SERVER_URL}/community-mirrors`);

  if (!response.ok) {
    throw new Error("Failed to fetch community mirrors");
  }

  return response.json();
};

export const getCommunityMirrorPipeline = async (
  projectId: number,
): Promise<CommunityMirrorPipeline> => {
  const response = await fetch(
    `${VITE_SERVER_URL}/community-mirrors/${projectId}/pipeline`,
  );

  if (!response.ok) {
    throw new Error("Failed to fetch pipeline details");
  }

  return response.json();
};

// COMMUNITY MIRRORS (Private endpoints)
export const toggleCommunityMirror = async (
  projectId: number,
  isAdding: boolean,
) => {
  const response = await fetch(
    `${VITE_SERVER_URL}/community-mirrors/${projectId}`,
    {
      method: isAdding ? "POST" : "DELETE",
      credentials: "include",
    },
  );
  if (!response.ok) {
    throw new Error(
      isAdding
        ? "Failed to add to community mirrors"
        : "Failed to remove from community mirrors",
    );
  }

  return response.json();
};

// FAUCET
export const getFaucetTokens = async (walletAddress: string) => {
  const response = await fetch(`${VITE_SERVER_URL}/user-faucet`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ walletAddress }),
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error("Failed to get funds from faucet");
  }
  return response.json();
};

// USER
export const getDynamicUser = async (dynamicUser: UserProfile) => {
  const response = await fetch(`${VITE_SERVER_URL}/user`, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      dynamicUUID: dynamicUser.userId,
      email: dynamicUser.email,
      provider: "dynamic",
    }),
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
};

export const getUser = async () => {
  const response = await fetch(`${VITE_SERVER_URL}/user`, {
    credentials: "include",
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
};

export const logOut = async () => {
  return await fetch(`${VITE_SERVER_URL}/logout`, {
    method: "POST",
    credentials: "include",
  });
};
